/**
 * User dashboard Home bundle: one fetch per UTC 6-hour window, per-user localStorage.
 * Windows: 00:00–06:00, 06:00–12:00, 12:00–18:00, 18:00–24:00 (all UTC).
 * After login, sessionStorage forces one network fetch (then slot rules apply).
 *
 * Cache key includes a tag derived from API_BASE_URL so switching local vs hosted API
 * (or live vs dev DB) does not reuse stale bundles from the previous backend.
 */

import { API_BASE_URL } from '../config/apiConfig';

/** Stable short tag from API root URL (localhost vs Azure, port, path). */
function apiBaseStorageTag() {
  const u = String(API_BASE_URL || '');
  let h = 0;
  for (let i = 0; i < u.length; i += 1) {
    h = (Math.imul(31, h) + u.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

const STORAGE_PREFIX = `dashboardHomeBundle_v6_${apiBaseStorageTag()}_`;
const SESSION_PROFILE_PREFIX = `dashboardUserSessionProfile_v1_${apiBaseStorageTag()}_`;
const PERSISTENT_RANK_PROGRESS_PREFIX = `dashboardHomeRankProgress_v1_${apiBaseStorageTag()}_`;
const SESSION_FORCE_KEY = 'userDashboardHomeForceNetwork';

/**
 * Start of the current UTC 6-hour window (00, 06, 12, or 18 UTC).
 * @param {number} [nowMs]
 * @returns {number} UTC epoch ms at that slot boundary
 */
export function getCurrentUtcSixHourSlotStartMs(nowMs = Date.now()) {
  const d = new Date(nowMs);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  const h = d.getUTCHours();
  const slotHour = Math.floor(h / 6) * 6;
  return Date.UTC(y, m, day, slotHour, 0, 0, 0);
}

/** @deprecated Use UTC slots; kept for any external imports. */
export const DASHBOARD_HOME_CACHE_TTL_MS = 6 * 60 * 60 * 1000;

function isCacheValidForCurrentUtcSlot(o) {
  if (!o || typeof o.utcSlotStartMs !== 'number') return false;
  return o.utcSlotStartMs === getCurrentUtcSixHourSlotStartMs();
}

function readDashboardStoragePayload(userId) {
  if (userId == null || !Number.isFinite(Number(userId))) return null;
  try {
    const s = localStorage.getItem(STORAGE_PREFIX + userId);
    if (!s) return null;
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function profileStorageKey(userId) {
  if (userId == null || !Number.isFinite(Number(userId))) return null;
  return SESSION_PROFILE_PREFIX + Number(userId);
}

export function readUserSessionProfileCache(userId) {
  const key = profileStorageKey(userId);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function readPersistentHomeRankProgressCache(userId) {
  if (userId == null || !Number.isFinite(Number(userId))) return null;
  try {
    const raw = localStorage.getItem(PERSISTENT_RANK_PROGRESS_PREFIX + Number(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      userId: Number(userId),
      t: Number(parsed.t) || 0,
      selfRankSummary:
        parsed.selfRankSummary && typeof parsed.selfRankSummary === 'object'
          ? parsed.selfRankSummary
          : null,
      communityConnections:
        parsed.communityConnections && typeof parsed.communityConnections === 'object'
          ? parsed.communityConnections
          : null,
      referralsByUserId:
        parsed.referralsByUserId && typeof parsed.referralsByUserId === 'object'
          ? parsed.referralsByUserId
          : {},
    };
  } catch {
    return null;
  }
}

export function mergePersistentHomeRankProgressCache(userId, partial) {
  if (userId == null || !Number.isFinite(Number(userId))) return;
  if (!partial || typeof partial !== 'object') return;
  try {
    const previous = readPersistentHomeRankProgressCache(userId) ?? {
      selfRankSummary: null,
      referralsByUserId: {},
    };
    const nextReferrals = { ...(previous.referralsByUserId ?? {}) };
    const incomingReferrals = partial.referralsByUserId;
    if (incomingReferrals && typeof incomingReferrals === 'object') {
      Object.entries(incomingReferrals).forEach(([id, row]) => {
        const numericId = Number(id);
        if (!Number.isFinite(numericId) || !row || typeof row !== 'object') return;
        nextReferrals[numericId] = {
          ...(nextReferrals[numericId] && typeof nextReferrals[numericId] === 'object'
            ? nextReferrals[numericId]
            : {}),
          ...row,
          userId: numericId,
        };
        delete nextReferrals[numericId].currentStreak;
        delete nextReferrals[numericId].adsWatchedToday;
        delete nextReferrals[numericId].goalPerDay;
        delete nextReferrals[numericId].todayGoalPercent;
      });
    }
    localStorage.setItem(
      PERSISTENT_RANK_PROGRESS_PREFIX + Number(userId),
      JSON.stringify({
        t: Date.now(),
        userId: Number(userId),
        selfRankSummary:
          partial.selfRankSummary && typeof partial.selfRankSummary === 'object'
            ? { ...(previous.selfRankSummary ?? {}), ...partial.selfRankSummary }
            : previous.selfRankSummary,
        communityConnections:
          partial.communityConnections && typeof partial.communityConnections === 'object'
            ? { ...(previous.communityConnections ?? {}), ...partial.communityConnections }
            : previous.communityConnections,
        referralsByUserId: nextReferrals,
      })
    );
  } catch {
    /* quota / private mode */
  }
}

export function writeUserSessionProfileCache(userId, profilePayload) {
  const key = profileStorageKey(userId);
  if (!key || !profilePayload || typeof profilePayload !== 'object') return;
  try {
    const profileData = profilePayload?.user ?? profilePayload;
    const referralCode = String(
      profileData?.ReferralCode ?? profileData?.referralCode ?? profilePayload?.ReferralCode ?? profilePayload?.referralCode ?? ''
    ).trim();
    localStorage.setItem(
      key,
      JSON.stringify({
        t: Date.now(),
        userId: Number(userId),
        profile: profilePayload,
        referralCode,
        referralLink: referralCode ? `https://dv-apps.com/signup?ref=${encodeURIComponent(referralCode)}` : '',
      })
    );
  } catch {
    /* quota / private mode */
  }
}

export function clearUserSessionProfileCaches(userId = null) {
  try {
    if (userId != null && Number.isFinite(Number(userId))) {
      localStorage.removeItem(SESSION_PROFILE_PREFIX + Number(userId));
      return;
    }
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(SESSION_PROFILE_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    /* ignore */
  }
}

export function clearUserDashboardHomeCaches(userId = null) {
  try {
    if (userId != null && Number.isFinite(Number(userId))) {
      localStorage.removeItem(STORAGE_PREFIX + Number(userId));
    } else {
      for (let i = localStorage.length - 1; i >= 0; i -= 1) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STORAGE_PREFIX)) {
          localStorage.removeItem(key);
        }
      }
    }
    clearUserDashboardHomeForceNetwork();
  } catch {
    /* ignore */
  }
}

export function readHomeCache(userId) {
  if (userId == null || !Number.isFinite(Number(userId))) return null;
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + userId);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!o || typeof o.t !== 'number') return null;
    if (!isCacheValidForCurrentUtcSlot(o)) return null;
    return {
      selfInfo: o.selfInfo ?? null,
      referrals: Array.isArray(o.referrals) ? o.referrals : [],
      fetchedAt: o.t,
      utcSlotStartMs: o.utcSlotStartMs,
      raw: o.raw && typeof o.raw === 'object' ? o.raw : null,
    };
  } catch {
    return null;
  }
}

/**
 * Whether a Home cache entry contains every API payload the Home page
 * normally fetches. A partial cache (e.g. a flaky mobile network where
 * only some sub-requests succeeded last time) returns `false` so the
 * caller knows to refetch instead of rendering empty-but-cached data.
 */
const REQUIRED_HOME_RAW_KEYS = ['profile', 'streak', 'refs', 'levels', 'coins', 'ledger'];

export function isHomeCacheComplete(cache) {
  if (!cache || !cache.selfInfo || !cache.raw || typeof cache.raw !== 'object') {
    return false;
  }
  for (const k of REQUIRED_HOME_RAW_KEYS) {
    if (cache.raw[k] === undefined) return false;
  }
  return true;
}

/**
 * Merge fields into `raw` for the current UTC slot (e.g. My Summary fetches after Home).
 * Creates a slot entry if none exists yet. Does not check session "force" — callers use after a fresh network response.
 *
 * Optional keys: walletBalance, paymentSummary, isChild, ledgerTodayTokens, etc.
 */
export function mergeDashboardRawFields(userId, partialRaw) {
  if (userId == null || !Number.isFinite(Number(userId))) return;
  if (!partialRaw || typeof partialRaw !== 'object') return;
  try {
    const slotMs = getCurrentUtcSixHourSlotStartMs();
    const o = readDashboardStoragePayload(userId);
    if (o && isCacheValidForCurrentUtcSlot(o)) {
      const next = {
        ...o,
        t: Date.now(),
        raw: { ...(o.raw && typeof o.raw === 'object' ? o.raw : {}), ...partialRaw },
      };
      localStorage.setItem(STORAGE_PREFIX + userId, JSON.stringify(next));
      return;
    }
    localStorage.setItem(
      STORAGE_PREFIX + userId,
      JSON.stringify({
        t: Date.now(),
        utcSlotStartMs: slotMs,
        selfInfo: null,
        referrals: [],
        raw: partialRaw,
      })
    );
  } catch {
    /* quota / private mode */
  }
}

/**
 * Shallow-merge fields into cached selfInfo (e.g. totalDreameyDiamonds after app-coins refresh).
 */
export function mergeHomeCacheSelfInfo(userId, partialSelfInfo) {
  if (userId == null || !Number.isFinite(Number(userId))) return;
  if (!partialSelfInfo || typeof partialSelfInfo !== 'object') return;
  try {
    const o = readDashboardStoragePayload(userId);
    if (!o || !isCacheValidForCurrentUtcSlot(o)) return;
    const prev = o.selfInfo && typeof o.selfInfo === 'object' ? o.selfInfo : {};
    const next = {
      ...o,
      t: Date.now(),
      selfInfo: { ...prev, ...partialSelfInfo },
    };
    localStorage.setItem(STORAGE_PREFIX + userId, JSON.stringify(next));
  } catch {
    /* quota / private mode */
  }
}

/**
 * @param {object|null} [raw] - API payloads shared with My Summary / My Activity (optional)
 */
export function writeHomeCache(userId, selfInfo, referrals, raw = null) {
  if (userId == null || !Number.isFinite(Number(userId))) return;
  try {
    const slotMs = getCurrentUtcSixHourSlotStartMs();
    const existing = readDashboardStoragePayload(userId);
    let mergedRaw = raw && typeof raw === 'object' ? { ...raw } : {};
    if (existing && isCacheValidForCurrentUtcSlot(existing) && existing.raw && typeof existing.raw === 'object') {
      mergedRaw = { ...existing.raw, ...mergedRaw };
    }
    const payload = {
      t: Date.now(),
      utcSlotStartMs: slotMs,
      selfInfo,
      referrals: Array.isArray(referrals) ? referrals : [],
    };
    if (Object.keys(mergedRaw).length > 0) {
      payload.raw = mergedRaw;
    }
    localStorage.setItem(STORAGE_PREFIX + userId, JSON.stringify(payload));
  } catch {
    /* quota / private mode */
  }
}

/**
 * Full dashboard bundle for reuse on other pages (same UTC slot, invalidated by login force flag).
 */
export function getDashboardRawIfFresh(userId) {
  if (shouldForceUserDashboardHomeNetwork()) return null;
  const c = readHomeCache(userId);
  return c?.raw ?? null;
}

/** Call after successful login so the next Home load fetches fresh data for that account. */
export function markUserDashboardHomeRequiresFreshFetch() {
  try {
    sessionStorage.setItem(SESSION_FORCE_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function shouldForceUserDashboardHomeNetwork() {
  try {
    return sessionStorage.getItem(SESSION_FORCE_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearUserDashboardHomeForceNetwork() {
  try {
    sessionStorage.removeItem(SESSION_FORCE_KEY);
  } catch {
    /* ignore */
  }
}
